import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, ChevronRight, Calendar, Euro, TrendingDown, 
  ArrowRight, FileSpreadsheet, Download, Wrench, Upload, 
  Calculator, AlertTriangle, CheckCircle, XCircle, RefreshCw,
  Eye, Play, Settings2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminFinancialDetail() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedMonths, setExpandedMonths] = useState({});
  const [monthlyDetails, setMonthlyDetails] = useState({}); // FASE 1: Dettaglio mensile
  const [loadingMonthDetail, setLoadingMonthDetail] = useState({});
  
  // Financial Tools State
  const [showTools, setShowTools] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [repairReport, setRepairReport] = useState(null);
  const [recalcReport, setRecalcReport] = useState(null);
  const [importReport, setImportReport] = useState(null);
  
  // Historical Import Form
  const [importForm, setImportForm] = useState({
    start_date: '',
    end_date: '',
    property_id: '',
    dry_run: true,
    import_mode: 'missing_only'
  });

  // Available years (current year and 2 previous)
  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2
  ];

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/financial/bookings-detail?anno=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      
      // Auto-expand current month
      const currentMonth = new Date().getMonth() + 1;
      if (selectedYear === new Date().getFullYear()) {
        setExpandedMonths({ [currentMonth]: true });
      }
    } catch (error) {
      console.error('Error fetching financial detail:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const toggleMonth = (meseNum) => {
    const isExpanding = !expandedMonths[meseNum];
    setExpandedMonths(prev => ({
      ...prev,
      [meseNum]: isExpanding
    }));
    
    // FASE 1: Carica dettaglio mensile quando si espande
    if (isExpanding && !monthlyDetails[meseNum]) {
      fetchMonthlyDetail(meseNum);
    }
  };
  
  // FASE 1: Carica dettaglio entrate/uscite mensile
  const fetchMonthlyDetail = async (meseNum) => {
    setLoadingMonthDetail(prev => ({ ...prev, [meseNum]: true }));
    try {
      const response = await axios.get(
        `${API}/admin/financial/monthly-detail/${selectedYear}/${meseNum}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMonthlyDetails(prev => ({ ...prev, [meseNum]: response.data }));
    } catch (error) {
      console.error('Error fetching monthly detail:', error);
    } finally {
      setLoadingMonthDetail(prev => ({ ...prev, [meseNum]: false }));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
  };

  const getSourceColor = (fonte) => {
    switch (fonte?.toLowerCase()) {
      case 'booking.com':
        return 'bg-blue-100 text-blue-700';
      case 'airbnb':
        return 'bg-red-100 text-red-700';
      case 'diretto':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const exportToCSV = () => {
    if (!data) return;
    
    let csv = 'Mese,Ospite,Camera,Check-in,Check-out,Notti,Fonte,Lordo,Commissione %,OTA €,Transazione €,Netto (L-O-T),Cedolare €,DA INCASSARE\n';
    
    data.mesi.forEach(mese => {
      mese.prenotazioni.forEach(p => {
        const daIncassare = p.lordo - p.commissione - p.transazione - p.cedolare;
        csv += `${mese.mese},${p.ospite},${p.camera},${p.data_arrivo},${p.data_partenza},${p.notti},${p.fonte},${p.lordo},${p.commissione_pct}%,${p.commissione},${p.transazione},${p.netto_pre_tasse},${p.cedolare},${daIncassare}\n`;
      });
    });
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `gestionale_${selectedYear}.csv`);
    link.click();
  };

  // ==================== FINANCIAL TOOLS ====================
  
  // Repair Direct Commissions - Dry Run
  const runRepairDryRun = async () => {
    setToolsLoading(true);
    setRepairReport(null);
    try {
      const response = await axios.post(`${API}/admin/financial/repair-direct-commissions`, 
        { dry_run: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setRepairReport(response.data);
      if (response.data.direct_bookings_with_wrong_commission === 0) {
        toast.success('Nessuna prenotazione diretta con commissioni errate');
      } else {
        toast.warning(`Trovate ${response.data.direct_bookings_with_wrong_commission} prenotazioni da correggere`);
      }
    } catch (error) {
      console.error('Repair dry run error:', error);
      toast.error('Errore nel controllo commissioni');
    } finally {
      setToolsLoading(false);
    }
  };

  // Repair Direct Commissions - Execute
  const executeRepair = async () => {
    if (!window.confirm('ATTENZIONE: Questa operazione modificherà i dati. Continuare?')) return;
    
    setToolsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/financial/repair-direct-commissions`, 
        { dry_run: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setRepairReport(response.data);
      toast.success(`Corrette ${response.data.direct_bookings_with_wrong_commission} prenotazioni`);
      fetchData(); // Refresh main data
    } catch (error) {
      console.error('Repair execute error:', error);
      toast.error('Errore nella correzione');
    } finally {
      setToolsLoading(false);
    }
  };

  // Monthly Recalculation - Dry Run
  const runRecalcDryRun = async () => {
    setToolsLoading(true);
    setRecalcReport(null);
    try {
      const response = await axios.post(`${API}/admin/financial/recalculate-monthly`, 
        { year: selectedYear, dry_run: true, include_cancelled: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setRecalcReport(response.data);
      toast.success('Analisi completata');
    } catch (error) {
      console.error('Recalc dry run error:', error);
      toast.error('Errore nel ricalcolo');
    } finally {
      setToolsLoading(false);
    }
  };

  // Historical Import - Dry Run
  const runImportDryRun = async () => {
    if (!importForm.start_date || !importForm.end_date) {
      toast.error('Seleziona date di inizio e fine');
      return;
    }
    
    setToolsLoading(true);
    setImportReport(null);
    try {
      const response = await axios.post(`${API}/admin/financial/historical-import`, 
        { ...importForm, dry_run: true },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setImportReport(response.data);
      if (response.data.summary.would_import === 0) {
        toast.info('Nessuna nuova prenotazione da importare');
      } else {
        toast.success(`Trovate ${response.data.summary.would_import} prenotazioni da importare`);
      }
    } catch (error) {
      console.error('Import dry run error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'anteprima import');
    } finally {
      setToolsLoading(false);
    }
  };

  // Historical Import - Execute
  const executeImport = async () => {
    if (!window.confirm('ATTENZIONE: Questa operazione importerà le prenotazioni storiche. Continuare?')) return;
    
    setToolsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/financial/historical-import`, 
        { ...importForm, dry_run: false },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setImportReport(response.data);
      toast.success(`Importate ${response.data.summary.would_import} prenotazioni`);
      fetchData(); // Refresh main data
    } catch (error) {
      console.error('Import execute error:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'import');
    } finally {
      setToolsLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Dettaglio Gestionale">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
        </div>
      </AdminLayout>
    );
  }

  const totali = data?.totali || {};
  const settings = data?.settings || {};

  return (
    <AdminLayout title="Dettaglio Gestionale">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <p className="text-sm text-gray-500">
              Dettaglio prenotazioni per mese con calcolo netto
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium bg-white"
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            
            <Button 
              variant="outline" 
              onClick={() => setShowTools(!showTools)}
              className="border-amber-500 text-amber-600 hover:bg-amber-50"
              data-testid="financial-tools-toggle"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              Strumenti
            </Button>
            
            <Button 
              variant="outline" 
              onClick={exportToCSV}
              className="border-[#C5A059] text-[#C5A059]"
            >
              <Download className="w-4 h-4 mr-2" />
              Esporta CSV
            </Button>
          </div>
        </div>

        {/* ==================== FINANCIAL TOOLS PANEL ==================== */}
        {showTools && (
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="financial-tools-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <Wrench className="w-5 h-5" />
                Strumenti di Gestione Finanziaria
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Tool 1: Repair Direct Commissions */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <Calculator className="w-4 h-4 text-amber-600" />
                  Correzione Commissioni Prenotazioni Dirette
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Le prenotazioni Dirette, Website, Telefono, WhatsApp non devono avere commissioni OTA.
                  Questo strumento identifica e corregge eventuali errori.
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={runRepairDryRun}
                    disabled={toolsLoading}
                    className="border-blue-400 text-blue-600 hover:bg-blue-50"
                    data-testid="repair-dry-run-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {toolsLoading ? 'Analisi...' : 'Anteprima'}
                  </Button>
                  {repairReport && repairReport.direct_bookings_with_wrong_commission > 0 && (
                    <Button 
                      variant="default" 
                      onClick={executeRepair}
                      disabled={toolsLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="repair-execute-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Correggi ({repairReport.direct_bookings_with_wrong_commission})
                    </Button>
                  )}
                </div>
                
                {/* Repair Report */}
                {repairReport && (
                  <div className="mt-4 p-3 bg-gray-50 rounded border text-sm" data-testid="repair-report">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <span className="text-gray-500">Analizzate:</span>
                        <span className="ml-2 font-medium">{repairReport.bookings_scanned}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Da correggere:</span>
                        <span className={`ml-2 font-medium ${repairReport.direct_bookings_with_wrong_commission > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {repairReport.direct_bookings_with_wrong_commission}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Commissioni errate:</span>
                        <span className="ml-2 font-medium text-red-600">{formatCurrency(repairReport.total_wrong_commission)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Stato:</span>
                        <span className="ml-2">
                          {repairReport.dry_run ? (
                            <span className="text-amber-600">Anteprima</span>
                          ) : (
                            <span className="text-green-600 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" /> Completato
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                    {repairReport.details?.length > 0 && (
                      <div className="mt-3 max-h-40 overflow-y-auto">
                        <p className="text-xs text-gray-500 mb-1">Prenotazioni interessate:</p>
                        {repairReport.details.slice(0, 10).map((d, i) => (
                          <div key={i} className="text-xs py-1 border-b border-gray-100">
                            {d.guest_name} - {d.arrival} - Comm. errata: {formatCurrency(d.wrong_commission_amount)}
                          </div>
                        ))}
                        {repairReport.details.length > 10 && (
                          <p className="text-xs text-gray-400 mt-1">... e altre {repairReport.details.length - 10}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Tool 2: Monthly Recalculation */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <RefreshCw className="w-4 h-4 text-amber-600" />
                  Ricalcolo Mensile {selectedYear}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Ricalcola i totali mensili escludendo le prenotazioni cancellate.
                  Le penali di cancellazione vengono mostrate separatamente.
                </p>
                <Button 
                  variant="outline" 
                  onClick={runRecalcDryRun}
                  disabled={toolsLoading}
                  className="border-blue-400 text-blue-600 hover:bg-blue-50"
                  data-testid="recalc-dry-run-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {toolsLoading ? 'Analisi...' : 'Analizza Differenze'}
                </Button>
                
                {/* Recalc Report */}
                {recalcReport && (
                  <div className="mt-4 p-3 bg-gray-50 rounded border text-sm" data-testid="recalc-report">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <span className="text-gray-500">Cancellate escluse:</span>
                        <span className="ml-2 font-medium text-amber-600">{recalcReport.summary.cancelled_bookings_excluded}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Penali cancellazione:</span>
                        <span className="ml-2 font-medium text-purple-600">{formatCurrency(recalcReport.summary.cancellation_revenue)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Prima (con cancellate)</p>
                        <p>Revenue: {formatCurrency(recalcReport.summary.revenue_before)}</p>
                        <p>Commissioni: {formatCurrency(recalcReport.summary.commission_before)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Dopo (senza cancellate)</p>
                        <p className="text-green-600">Revenue: {formatCurrency(recalcReport.summary.revenue_after)}</p>
                        <p className="text-green-600">Commissioni: {formatCurrency(recalcReport.summary.commission_after)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Tool 3: Historical Beds24 Import */}
              <div className="p-4 bg-white rounded-lg border border-amber-200">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <Upload className="w-4 h-4 text-amber-600" />
                  Import Storico Beds24
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Importa prenotazioni storiche da Beds24 in modo sicuro. 
                  Non sovrascrive dati esistenti e non attiva comunicazioni automatiche.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Data Inizio</label>
                    <input
                      type="date"
                      value={importForm.start_date}
                      onChange={(e) => setImportForm({...importForm, start_date: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                      data-testid="import-start-date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Data Fine</label>
                    <input
                      type="date"
                      value={importForm.end_date}
                      onChange={(e) => setImportForm({...importForm, end_date: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                      data-testid="import-end-date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Modalità</label>
                    <select
                      value={importForm.import_mode}
                      onChange={(e) => setImportForm({...importForm, import_mode: e.target.value})}
                      className="w-full border rounded px-3 py-2 text-sm"
                      data-testid="import-mode"
                    >
                      <option value="missing_only">Solo mancanti</option>
                      <option value="review_differences">Revisiona differenze</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={runImportDryRun}
                    disabled={toolsLoading || !importForm.start_date || !importForm.end_date}
                    className="border-blue-400 text-blue-600 hover:bg-blue-50"
                    data-testid="import-dry-run-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {toolsLoading ? 'Analisi...' : 'Anteprima'}
                  </Button>
                  {importReport && importReport.summary.would_import > 0 && (
                    <Button 
                      variant="default" 
                      onClick={executeImport}
                      disabled={toolsLoading}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="import-execute-btn"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Importa ({importReport.summary.would_import})
                    </Button>
                  )}
                </div>
                
                {/* Import Report */}
                {importReport && (
                  <div className="mt-4 p-3 bg-gray-50 rounded border text-sm" data-testid="import-report">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <div>
                        <span className="text-gray-500">Trovate Beds24:</span>
                        <span className="ml-2 font-medium">{importReport.beds24_bookings_found}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Da importare:</span>
                        <span className={`ml-2 font-medium ${importReport.summary.would_import > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                          {importReport.summary.would_import}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Già esistenti:</span>
                        <span className="ml-2 font-medium text-blue-600">{importReport.summary.would_skip}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Da revisionare:</span>
                        <span className={`ml-2 font-medium ${importReport.summary.needs_manual_review > 0 ? 'text-amber-600' : 'text-gray-600'}`}>
                          {importReport.summary.needs_manual_review}
                        </span>
                      </div>
                    </div>
                    
                    {importReport.needs_review?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-amber-600 font-medium mb-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Prenotazioni con differenze (revisione manuale):
                        </p>
                        <div className="max-h-32 overflow-y-auto">
                          {importReport.needs_review.slice(0, 5).map((r, i) => (
                            <div key={i} className="text-xs py-1 border-b border-gray-100">
                              {r.guest_name} - {r.arrival}
                              <span className="text-gray-400 ml-2">
                                ({r.differences?.map(d => d.field).join(', ')})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {importReport.to_import?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-green-600 font-medium mb-1">Nuove prenotazioni da importare:</p>
                        <div className="max-h-32 overflow-y-auto">
                          {importReport.to_import.slice(0, 5).map((b, i) => (
                            <div key={i} className="text-xs py-1 border-b border-gray-100">
                              {b.guest_name} - {b.arrival} → {b.departure} - {formatCurrency(b.total)}
                            </div>
                          ))}
                          {importReport.to_import.length > 5 && (
                            <p className="text-xs text-gray-400 mt-1">... e altre {importReport.to_import.length - 5}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Safety Notice */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-800 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Sicurezza:</strong> Tutte le operazioni mostrano prima un&apos;anteprima. 
                  L&apos;import storico NON modifica prenotazioni esistenti e NON attiva comunicazioni automatiche.
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-blue-600 font-medium">LORDO {selectedYear}</p>
              <p className="text-xl font-bold text-blue-800">{formatCurrency(totali.totale_lordo)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-red-600 font-medium">COMMISSIONI</p>
              <p className="text-xl font-bold text-red-700">-{formatCurrency(totali.totale_commissioni)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-orange-600 font-medium">TRANSAZIONI</p>
              <p className="text-xl font-bold text-orange-700">-{formatCurrency(totali.totale_transazioni)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-purple-600 font-medium">CEDOLARE {settings.cedolare_secca}%</p>
              <p className="text-xl font-bold text-purple-700">-{formatCurrency(totali.totale_cedolare)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-green-600 font-medium">NETTO FINALE</p>
              <p className="text-xl font-bold text-green-700">{formatCurrency(totali.totale_netto)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Info bar */}
        <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-600 flex flex-wrap gap-4">
          <span><strong>{totali.num_prenotazioni}</strong> prenotazioni</span>
          <span><strong>{totali.notti_totali}</strong> notti totali</span>
          <span>Commissione Booking: <strong>{settings.commissione_booking}%</strong></span>
          <span>Commissione Airbnb: <strong>{settings.commissione_airbnb}%</strong></span>
          <span>Transazione: <strong>{settings.costo_transazione}%</strong></span>
        </div>

        {/* Monthly Breakdown */}
        <div className="space-y-4">
          {data?.mesi?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Nessuna prenotazione per il {selectedYear}</p>
              </CardContent>
            </Card>
          ) : (
            data?.mesi?.map((mese) => (
              <Card key={mese.mese_num} className="overflow-hidden">
                {/* Month Header - Clickable */}
                <button
                  onClick={() => toggleMonth(mese.mese_num)}
                  className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-4">
                    {expandedMonths[mese.mese_num] ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">{mese.mese} {mese.anno}</h3>
                      <p className="text-sm text-gray-500">
                        {mese.prenotazioni.length} prenotazioni · {mese.notti} notti
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <p className="text-xs text-gray-400">Lordo</p>
                      <p className="font-semibold text-blue-600">{formatCurrency(mese.totale_lordo)}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-300" />
                    <div>
                      <p className="text-xs text-gray-400">Netto</p>
                      <p className="font-bold text-green-600">{formatCurrency(mese.totale_netto)}</p>
                    </div>
                  </div>
                </button>

                {/* Expanded Content */}
                {expandedMonths[mese.mese_num] && (
                  <div className="border-t">
                    {/* FASE 1: Conto Economico Mensile */}
                    {loadingMonthDetail[mese.mese_num] ? (
                      <div className="p-6 flex justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#C5A059]"></div>
                      </div>
                    ) : monthlyDetails[mese.mese_num] && (
                      <div className="p-4 bg-gradient-to-r from-gray-50 to-white border-b">
                        <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                          <Euro className="w-4 h-4 text-[#C5A059]" />
                          Conto Economico {mese.mese} {mese.anno}
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* ENTRATE */}
                          <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                            <h5 className="text-sm font-semibold text-green-700 mb-2">ENTRATE</h5>
                            <div className="space-y-1 text-sm">
                              {monthlyDetails[mese.mese_num].entrate.booking_com > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Booking.com</span>
                                  <span className="font-medium">{formatCurrency(monthlyDetails[mese.mese_num].entrate.booking_com)}</span>
                                </div>
                              )}
                              {monthlyDetails[mese.mese_num].entrate.airbnb > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Airbnb</span>
                                  <span className="font-medium">{formatCurrency(monthlyDetails[mese.mese_num].entrate.airbnb)}</span>
                                </div>
                              )}
                              {monthlyDetails[mese.mese_num].entrate.dirette > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Dirette</span>
                                  <span className="font-medium">{formatCurrency(monthlyDetails[mese.mese_num].entrate.dirette)}</span>
                                </div>
                              )}
                              {monthlyDetails[mese.mese_num].entrate.expedia > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Expedia</span>
                                  <span className="font-medium">{formatCurrency(monthlyDetails[mese.mese_num].entrate.expedia)}</span>
                                </div>
                              )}
                              {monthlyDetails[mese.mese_num].entrate.altri > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Altri</span>
                                  <span className="font-medium">{formatCurrency(monthlyDetails[mese.mese_num].entrate.altri)}</span>
                                </div>
                              )}
                              <div className="border-t pt-1 mt-2 flex justify-between font-bold text-green-700">
                                <span>Totale Lordo</span>
                                <span>{formatCurrency(monthlyDetails[mese.mese_num].entrate.totale_lordo)}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* TRATTENUTE */}
                          <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
                            <h5 className="text-sm font-semibold text-orange-700 mb-2">TRATTENUTE</h5>
                            <div className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-600">Commissioni OTA</span>
                                <span className="font-medium text-red-600">-{formatCurrency(monthlyDetails[mese.mese_num].trattenute.commissioni_ota)}</span>
                              </div>
                              {monthlyDetails[mese.mese_num].trattenute.commissioni_transazione > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Transazioni</span>
                                  <span className="font-medium text-red-600">-{formatCurrency(monthlyDetails[mese.mese_num].trattenute.commissioni_transazione)}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-gray-600">Cedolare Secca</span>
                                <span className="font-medium text-red-600">-{formatCurrency(monthlyDetails[mese.mese_num].trattenute.cedolare_secca)}</span>
                              </div>
                              <div className="border-t pt-1 mt-2 flex justify-between font-bold text-orange-700">
                                <span>Totale Trattenute</span>
                                <span>-{formatCurrency(monthlyDetails[mese.mese_num].trattenute.totale_trattenute)}</span>
                              </div>
                            </div>
                          </div>
                          
                          {/* USCITE OPERATIVE */}
                          <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                            <h5 className="text-sm font-semibold text-red-700 mb-2">SPESE OPERATIVE</h5>
                            <div className="space-y-1 text-sm">
                              {Object.entries(monthlyDetails[mese.mese_num].uscite.per_categoria || {}).map(([cat, val]) => (
                                <div key={cat} className="flex justify-between">
                                  <span className="text-gray-600 capitalize">{cat}</span>
                                  <span className="font-medium text-red-600">-{formatCurrency(val)}</span>
                                </div>
                              ))}
                              {Object.keys(monthlyDetails[mese.mese_num].uscite.per_categoria || {}).length === 0 && (
                                <p className="text-gray-400 text-xs italic">Nessuna spesa registrata</p>
                              )}
                              <div className="border-t pt-1 mt-2 flex justify-between font-bold text-red-700">
                                <span>Totale Spese</span>
                                <span>-{formatCurrency(monthlyDetails[mese.mese_num].uscite.totale_uscite)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* RIEPILOGO */}
                        <div className="mt-4 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-4 border border-green-300">
                          <div className="grid grid-cols-4 gap-4 text-center">
                            <div>
                              <p className="text-xs text-gray-500">Lordo</p>
                              <p className="font-bold text-blue-700">{formatCurrency(monthlyDetails[mese.mese_num].riepilogo.totale_lordo)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Dopo Trattenute</p>
                              <p className="font-bold text-orange-700">{formatCurrency(monthlyDetails[mese.mese_num].riepilogo.netto_dopo_trattenute)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Spese Operative</p>
                              <p className="font-bold text-red-700">-{formatCurrency(monthlyDetails[mese.mese_num].riepilogo.totale_spese_operative)}</p>
                            </div>
                            <div className="bg-white rounded-lg p-2">
                              <p className="text-xs text-green-600 font-semibold">PROFITTO</p>
                              <p className="text-xl font-bold text-green-700">{formatCurrency(monthlyDetails[mese.mese_num].riepilogo.profitto_mensile)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Table Header - Prenotazioni */}
                    {/* Totals Row at TOP */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-2 grid grid-cols-13 gap-2 text-xs font-bold text-white uppercase">
                      <div className="col-span-2">TOTALI</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center">{mese.notti} notti</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-right">{formatCurrency(mese.totale_lordo)}</div>
                      <div className="col-span-1 text-right">-{formatCurrency(mese.totale_commissioni)}</div>
                      <div className="col-span-1 text-right">-{formatCurrency(mese.totale_transazioni)}</div>
                      <div className="col-span-1 text-right">{formatCurrency(mese.totale_lordo - mese.totale_commissioni - mese.totale_transazioni)}</div>
                      <div className="col-span-1 text-right">-{formatCurrency(mese.totale_cedolare)}</div>
                      <div className="col-span-2 text-right text-yellow-200">{formatCurrency(mese.totale_lordo - mese.totale_commissioni - mese.totale_transazioni - mese.totale_cedolare)}</div>
                    </div>
                    
                    {/* Column Headers */}
                    <div className="bg-gray-100 px-4 py-2 grid grid-cols-13 gap-2 text-xs font-semibold text-gray-600 uppercase">
                      <div className="col-span-2">Ospite</div>
                      <div className="col-span-1">Date</div>
                      <div className="col-span-1">Notti</div>
                      <div className="col-span-1">Fonte</div>
                      <div className="col-span-1 text-right">Lordo</div>
                      <div className="col-span-1 text-right">OTA</div>
                      <div className="col-span-1 text-right">Trans.</div>
                      <div className="col-span-1 text-right" title="Lordo - OTA - Transazioni">Netto (L-O-T)</div>
                      <div className="col-span-1 text-right">Cedol.</div>
                      <div className="col-span-2 text-right" title="Lordo - OTA - Transazioni - Cedolare">DA INCASSARE</div>
                    </div>

                    {/* Booking Rows */}
                    {mese.prenotazioni.map((p, idx) => {
                      const daIncassare = p.lordo - p.commissione - p.transazione - p.cedolare;
                      return (
                        <div 
                          key={p.id || idx}
                          className={`px-4 py-3 grid grid-cols-13 gap-2 text-sm items-center
                            ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            hover:bg-yellow-50 transition-colors`}
                        >
                          <div className="col-span-2">
                            <p className="font-medium text-gray-900 truncate">{p.ospite}</p>
                            <p className="text-xs text-gray-400">{p.codice}</p>
                          </div>
                          <div className="col-span-1 text-xs">
                            {formatDate(p.data_arrivo)} → {formatDate(p.data_partenza)}
                          </div>
                          <div className="col-span-1 text-center">
                            <span className="bg-gray-200 px-2 py-0.5 rounded text-xs font-medium">
                              {p.notti}
                            </span>
                          </div>
                          <div className="col-span-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${getSourceColor(p.fonte)}`}>
                              {p.fonte}
                            </span>
                          </div>
                          <div className="col-span-1 text-right font-medium">
                            {formatCurrency(p.lordo)}
                          </div>
                          <div className="col-span-1 text-right text-red-600 text-xs">
                            -{formatCurrency(p.commissione)}
                            <br />
                            <span className="text-gray-400">({p.commissione_pct}%)</span>
                          </div>
                          <div className="col-span-1 text-right text-orange-600 text-xs">
                            -{formatCurrency(p.transazione)}
                          </div>
                          <div className="col-span-1 text-right text-gray-600">
                            {formatCurrency(p.netto_pre_tasse)}
                          </div>
                          <div className="col-span-1 text-right text-purple-600 text-xs">
                            -{formatCurrency(p.cedolare)}
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="font-bold text-green-600 text-base">
                              {formatCurrency(daIncassare)}
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {/* Month Totals */}
                    <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-4 py-3 grid grid-cols-13 gap-2 text-sm text-white font-medium">
                      <div className="col-span-2">TOTALE {mese.mese.toUpperCase()}</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-center">{mese.notti}</div>
                      <div className="col-span-1"></div>
                      <div className="col-span-1 text-right">{formatCurrency(mese.totale_lordo)}</div>
                      <div className="col-span-1 text-right text-red-300">-{formatCurrency(mese.totale_commissioni)}</div>
                      <div className="col-span-1 text-right text-orange-300">-{formatCurrency(mese.totale_transazioni)}</div>
                      <div className="col-span-1 text-right">{formatCurrency(mese.totale_lordo - mese.totale_commissioni - mese.totale_transazioni)}</div>
                      <div className="col-span-1 text-right text-purple-300">-{formatCurrency(mese.totale_cedolare)}</div>
                      <div className="col-span-2 text-right text-green-300 text-lg">{formatCurrency(mese.totale_lordo - mese.totale_commissioni - mese.totale_transazioni - mese.totale_cedolare)}</div>
                    </div>
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Year Total Footer */}
        {data?.mesi?.length > 0 && (
          <Card className="bg-gradient-to-r from-[#C5A059] to-[#D4AF61] text-white">
            <CardContent className="p-4">
              <div className="grid grid-cols-6 gap-4 text-center">
                <div>
                  <p className="text-xs opacity-80">TOTALE ANNO</p>
                  <p className="text-2xl font-bold">{selectedYear}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">LORDO</p>
                  <p className="text-xl font-bold">{formatCurrency(totali.totale_lordo)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">COMMISSIONI</p>
                  <p className="text-xl font-bold">-{formatCurrency(totali.totale_commissioni)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">CEDOLARE</p>
                  <p className="text-xl font-bold">-{formatCurrency(totali.totale_cedolare)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">NETTO FINALE</p>
                  <p className="text-2xl font-bold">{formatCurrency(totali.totale_netto)}</p>
                </div>
                <div>
                  <p className="text-xs opacity-80">PRENOTAZIONI</p>
                  <p className="text-xl font-bold">{totali.num_prenotazioni}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
